import React, { useState, useCallback, useMemo } from 'react';
import {
  Shield,
  Lock,
  UserCheck,
  Clock,
  Settings,
  AlertTriangle,
  CheckCircle,
  Activity,
  Key,
  Database,
  Save,
  LogIn,
  Users,
  RefreshCw, // ADDED: Missing import for RefreshCw icon
} from 'lucide-react';

// --- Reusable UI Components ---

// Button Component
const ButtonPrimary = ({ onClick, children, className = '', disabled = false, icon: Icon }) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-4 py-2 text-sm font-medium rounded-xl shadow-md transition-all duration-200 flex items-center justify-center whitespace-nowrap focus:outline-none focus:ring-4 focus:ring-blue-500/50 
                  ${disabled 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'}
                  ${className}`}
    >
      {Icon && <Icon className="w-4 h-4 mr-2" />}
      {children}
    </button>
  );
};

// Toggle Switch Component
const ToggleSwitch = ({ enabled, setEnabled, label }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
    <span className="text-sm font-medium text-gray-700">{label}</span>
    <button
      onClick={() => setEnabled(!enabled)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
        enabled ? 'bg-blue-600 focus:ring-blue-500' : 'bg-gray-200 focus:ring-gray-400'
      }`}
    >
      <span className="sr-only">Toggle {label}</span>
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

// Setting Block/Card Component
const SettingBlock = ({ icon: Icon, title, description, children }) => (
  <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
    <div className="flex items-center mb-4">
      <div className="p-2 rounded-full bg-blue-500/10 text-blue-600 mr-3">
        <Icon className="w-5 h-5" />
      </div>
      <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
    </div>
    <p className="text-sm text-gray-500 mb-4">{description}</p>
    <div className="space-y-2">
      {children}
    </div>
  </div>
);

// --- Mock Data ---

const MOCK_ACTIVITY_LOG = [
  { time: '10:30 AM', user: 'Admin Jane', action: 'Disabled MFA on User 101' },
  { time: '10:15 AM', user: 'System', action: 'Password rotation policy applied' },
  { time: '9:45 AM', user: 'User 505', action: 'Login successful (2FA)' },
  { time: '8:00 AM', user: 'Admin John', action: 'Updated security contact email' },
];

// --- Main Component ---

export default function SecurityManager() {
  const [is2FAEnabled, setIs2FAEnabled] = useState(true);
  const [isSessionLoggingEnabled, setIsSessionLoggingEnabled] = useState(true);
  const [sessionTimeout, setSessionTimeout] = useState(30); // minutes
  const [isSaving, setIsSaving] = useState(false);
  const [isSystemHealthy, setIsSystemHealthy] = useState(true); // Mock status

  // Calculates the overall security score based on current settings
  const securityScore = useMemo(() => {
    let score = 0;
    if (is2FAEnabled) score += 40;
    if (isSessionLoggingEnabled) score += 30;
    if (sessionTimeout > 15 && sessionTimeout <= 60) score += 30;
    return score;
  }, [is2FAEnabled, isSessionLoggingEnabled, sessionTimeout]);

  const securityStatus = useMemo(() => {
    if (securityScore >= 90) return { status: 'Excellent', color: 'text-green-600', icon: CheckCircle };
    if (securityScore >= 60) return { status: 'Good', color: 'text-yellow-600', icon: AlertTriangle };
    // The icon is returned as a component function reference
    return { status: 'Needs Attention', color: 'text-red-600', icon: AlertTriangle }; 
  }, [securityScore]);
  
  const handleSave = useCallback(() => {
    setIsSaving(true);
    // Simulate API call delay
    setTimeout(() => {
      console.log('Security settings saved:', { is2FAEnabled, isSessionLoggingEnabled, sessionTimeout });
      setIsSaving(false);
      // Optional: Show a success notification
    }, 1500);
  }, [is2FAEnabled, isSessionLoggingEnabled, sessionTimeout]);

  return (
    <div className="p-4 sm:p-8 bg-gray-50 min-h-screen font-sans">
      
      {/* HEADER */}
      <div className="mb-8 border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Shield className="w-7 h-7 mr-3 text-blue-600" />
          Security Center
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage system-wide security policies, monitor activity, and review audit logs.
        </p>
      </div>

      {/* SECURITY OVERVIEW CARD */}
      <div className="mb-8">
        <div className="bg-white p-6 rounded-xl shadow-lg flex items-center justify-between border-l-4 border-blue-500">
          <div>
            <p className="text-sm font-medium text-gray-500">Current Security Score</p>
            <div className="flex items-center mt-1">
              <span className="text-3xl font-extrabold text-gray-900">{securityScore}%</span>
              <div className={`ml-4 flex items-center text-sm font-semibold ${securityStatus.color}`}>
                {/* FIXED: Called securityStatus.icon as a component */}
                <securityStatus.icon className="w-4 h-4 mr-1" /> 
                {securityStatus.status}
              </div>
            </div>
          </div>
          <ButtonPrimary onClick={() => setIsSystemHealthy(prev => !prev)} icon={RefreshCw} className="hidden sm:flex">
            {isSystemHealthy ? 'Simulate Breach' : 'Reset Status'}
          </ButtonPrimary>
        </div>
      </div>

      {/* SETTINGS AND LOGS GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* COLUMN 1: AUTHENTICATION SETTINGS */}
        <div className="lg:col-span-2 space-y-8">
          <SettingBlock 
            icon={Lock} 
            title="Authentication Policies" 
            description="Configure login requirements for administrators and standard users."
          >
            <ToggleSwitch
              label="Require Two-Factor Authentication (2FA) for all Admins"
              enabled={is2FAEnabled}
              setEnabled={setIs2FAEnabled}
            />
            <ToggleSwitch
              label="Enable Login Session Logging"
              enabled={isSessionLoggingEnabled}
              setEnabled={setIsSessionLoggingEnabled}
            />
            
            {/* Session Timeout Input */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
              <span className="text-sm font-medium text-gray-700">Max Session Timeout (Minutes)</span>
              <input 
                type="number" 
                value={sessionTimeout}
                onChange={(e) => setSessionTimeout(Math.max(1, parseInt(e.target.value, 10) || 0))}
                min="1"
                max="120"
                className="w-20 p-1 border border-gray-300 rounded-lg text-sm text-right focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Password Policy Link/Info */}
            <div className="flex items-center justify-between pt-3">
              <span className="text-sm font-medium text-gray-700">Password Complexity Policy</span>
              <a href="#" className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center">
                Review Policy Details <Key className="w-3 h-3 ml-1" />
              </a>
            </div>

          </SettingBlock>

          {/* DATA & ENCRYPTION SETTINGS */}
          <SettingBlock 
            icon={Database} 
            title="Data & Encryption" 
            description="View the status of data-at-rest encryption and key management."
          >
            <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                <span className="text-sm font-medium text-gray-700">Database Encryption Status</span>
                <span className="text-xs font-semibold text-green-600 flex items-center">
                    <CheckCircle className="w-3 h-3 mr-1" /> Enabled (AES-256)
                </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                <span className="text-sm font-medium text-gray-700">Key Rotation Cycle</span>
                <span className="text-xs font-semibold text-gray-500">Every 90 Days</span>
            </div>
          </SettingBlock>
          
        </div>

        {/* COLUMN 2: ACTIVITY LOG */}
        <SettingBlock 
          icon={Activity} 
          title="Recent Activity Log" 
          description="A summary of the most critical security and configuration events."
          className="lg:col-span-1"
        >
          {MOCK_ACTIVITY_LOG.map((log, index) => (
            <div key={index} className="flex items-start text-sm py-2 border-b border-gray-100 last:border-b-0">
              <LogIn className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div className="ml-3">
                <p className="text-gray-800 font-medium leading-tight">{log.action}</p>
                <p className="text-xs text-gray-500 leading-tight mt-0.5">
                    <span className="font-semibold">{log.user}</span> at {log.time}
                </p>
              </div>
            </div>
          ))}
          <a href="#" className="text-xs text-blue-600 hover:text-blue-700 font-medium block pt-2 text-right">
             View Full Audit Log
          </a>
        </SettingBlock>
      </div>

      {/* SAVE ACTIONS */}
      <div className="mt-10 pt-6 border-t border-gray-200 flex justify-end">
        <ButtonPrimary 
          onClick={handleSave} 
          disabled={isSaving} 
          icon={Save}
          className="min-w-40"
        >
          {isSaving ? 'Saving Changes...' : 'Save Settings'}
        </ButtonPrimary>
      </div>
    </div>
  );
}